import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@vaiu/ui/button';
import { UserX, LogIn, UserPlus } from 'lucide-react';
import { getAppTagline } from '@/utils/domainDetection';

interface AuthenticationSectionProps {
  onGuestClick?: () => void;
  onSignInClick?: () => void;
  className?: string;
}

const AuthenticationSection: React.FC<AuthenticationSectionProps> = ({ 
  onGuestClick,
  onSignInClick,
  className = ''
}) => {
  const navigate = useNavigate();

  const handleGuestClick = () => {
    if (onGuestClick) {
      onGuestClick();
    } else {
      // Default behavior - trigger guest login directly
      const guestButton = document.querySelector('[data-guest-login-button]') as HTMLButtonElement;
      if (guestButton) {
        guestButton.click();
      }
    }
  };

  const handleSignInClick = () => {
    if (onSignInClick) {
      onSignInClick();
    } else {
      // Default behavior - navigate to login page
      navigate('/login');
    }
  };

  return (
    <div className={`bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50 ${className}`}>
      <div className="max-w-4xl mx-auto px-2 sm:px-4 py-2 sm:py-3">
        {/* Responsive button group - stack on very small screens */}
        <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-1.5 sm:gap-2">
          {/* Continue as Guest - Outlined button */}
          <Button
            onClick={handleGuestClick}
            size="default"
            variant="outline"
            className="px-3 sm:px-6 py-2 sm:py-2.5 border-2 border-blue-500 text-blue-600 hover:bg-blue-50 hover:border-blue-600 font-medium rounded-lg transition-all duration-200 text-sm sm:text-base"
          >
            <UserX className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span className="truncate">Guest</span>
            <span className="hidden min-[360px]:inline ml-1">Access</span>
          </Button>

          {/* Sign In - Ghost button */}
          <Button
            onClick={handleSignInClick}
            variant="ghost"
            size="default"
            className="px-3 sm:px-6 py-2 sm:py-2.5 text-gray-700 hover:text-gray-900 hover:bg-gray-100 font-medium rounded-lg transition-all duration-200 text-sm sm:text-base"
          >
            <LogIn className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span>Sign In</span>
          </Button>

          {/* Sign up - Primary button */}
          <Button
            onClick={() => navigate('/signup')}
            size="default"
            className="px-3 sm:px-6 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 text-sm sm:text-base"
          >
            <UserPlus className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span>Sign up</span>
          </Button>
        </div>

        {/* Tagline - responsive text size */}
        <div className="text-center mt-2 sm:mt-3 text-xs sm:text-sm text-gray-500 px-2">
          {getAppTagline()}
        </div>
      </div>
    </div>
  );
};

export default AuthenticationSection;
